import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DeleteAthleteDialog } from "@/components/DeleteAthleteDialog";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, Upload, HeartPulse, Sparkles, CalendarCheck, ClipboardList, Trophy, TrendingUp } from "lucide-react";
import AthleteCategoryPanel from "@/components/AthleteCategoryPanel";
import ParalympicProfileSection from "@/components/ParalympicProfileSection";
import AthleteWeaponsPanel from "@/components/AthleteWeaponsPanel";
import AthleteTestSummary from "@/components/AthleteTestSummary";
import AthleteLessonsCard from "@/components/AthleteLessonsCard";
import AthleteBoutsCard from "@/components/AthleteBoutsCard";
import SkillProfile from "@/components/skills/SkillProfile";
import { AthleteProfileSkeleton } from "@/components/Skeleton";
import { useAuth } from "@/context/AuthContext";

async function compressImage(file, maxSize = 400, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    reader.readAsDataURL(file);
  });
}

const STATUS_LABEL = {
  disponibile: "Disponibile",
  allenamento_ridotto: "Allenamento ridotto",
  infortunato: "Infortunato",
  return_to_play: "Return to play",
  assente: "Assente",
};

function fmtVal(v) { return typeof v === "number" ? v.toLocaleString("it-IT", { maximumFractionDigits: 2 }) : "—"; }

function labelDate(d) {
  if (!d) return "—";
  const s = d.slice(0, 10);
  try {
    return new Date(s + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
  } catch { return s; }
}

export default function AthleteProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = user && ["admin", "coach", "trainer"].includes(user.role);
  const [athlete, setAthlete] = useState(null);
  const [groups, setGroups] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [goals, setGoals] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [weaponRows, setWeaponRows] = useState([]);
  const [testSummary, setTestSummary] = useState(null);
  const [newGoal, setNewGoal] = useState({ description: "", category: "", deadline: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const [a, t, g, gr, inj, comps, regs, ai] = await Promise.all([
      api.get(`/athletes/${id}`),
      api.get(`/athletes/${id}/timeline`),
      api.get(`/goals`),
      api.get(`/groups`),
      api.get(`/injuries`),
      api.get(`/competitions`).catch(() => ({ data: [] })),
      api.get(`/attendance/registers`, { params: { athlete_id: id } }).catch(() => ({ data: [] })),
      api.get(`/ai/coach/${id}/history`).catch(() => ({ data: [] })),
    ]);
    setAthlete(a.data);
    api.get(`/athletes/${id}/weapons`).then((r) => setWeaponRows(r.data || [])).catch(() => setWeaponRows([]));
    api.get(`/athletes/${id}/test-summary`).then((r) => setTestSummary(r.data)).catch(() => setTestSummary(null));
    setTimeline(t.data);
    setGoals(g.data.filter((x) => x.athlete_id === id));
    setGroups(gr.data);
    setInjuries(inj.data.filter((x) => x.athlete_id === id));
    setCompetitions(comps.data || []);
    setRegisters(regs.data || []);
    if (ai.data?.length) setAiSuggestion(ai.data[0]);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const save = async () => {
    try {
      await api.put(`/athletes/${id}`, athlete);
      try { await api.post(`/athletes/${id}/recompute-category`, {}); } catch (_) {}
      toast.success("Salvato");
    } catch { toast.error("Errore"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/athletes/${id}`);
      toast.success("Atleta eliminato");
      nav("/atleti");
    } catch (e) { toast.error(e.response?.data?.detail?.message || "Errore"); }
  };

  const addGoal = async () => {
    if (!newGoal.description) return;
    await api.post("/goals", { ...newGoal, athlete_id: id });
    setNewGoal({ description: "", category: "", deadline: "" });
    load();
  };

  const deleteGoal = async (gid) => { await api.delete(`/goals/${gid}`); load(); };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("File troppo grande (max 8MB)");
    try {
      const dataUrl = await compressImage(file);
      const updated = { ...athlete, photo_url: dataUrl };
      setAthlete(updated);
      await api.put(`/athletes/${id}`, updated);
      toast.success("Foto aggiornata");
    } catch { toast.error("Errore caricamento foto"); }
  };

  const removePhoto = async () => {
    const updated = { ...athlete, photo_url: null };
    setAthlete(updated);
    await api.put(`/athletes/${id}`, updated);
    toast.success("Foto rimossa");
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/ai/coach/${id}`);
      setAiSuggestion(data);
      toast.success("Suggerimenti AI generati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore AI");
    } finally { setAiLoading(false); }
  };

  // --- KPIs derivati (client-side) --------------------------------------
  const kpis = useMemo(() => {
    const now = Date.now();
    // v1.10.0 · Fix integrità presenze: conteggio su tutta la stagione, non solo ultimi 30gg
    const cutoff = now - 3650 * 24 * 3600 * 1000;

    // Merge presence sources: nuovi registri (attendance_registers) + timeline legacy
    const presenceEvents = [];
    (registers || []).forEach((r) => {
      const entry = (r.entries || []).find((e) => e.athlete_id === id);
      if (entry && r.date) {
        presenceEvents.push({ date: r.date, status: entry.status, source: "register" });
      }
    });
    (timeline || []).filter((e) => e.type === "presenza").forEach((e) => {
      presenceEvents.push({ date: (e.date || "").slice(0, 10), status: e.title, source: "legacy" });
    });
    presenceEvents.sort((a, b) => (b.date || "").localeCompare(a.date || ""));

    const recent = presenceEvents.filter((e) => {
      const t = Date.parse(e.date || "");
      return !isNaN(t) && t >= cutoff;
    });
    const presenceStates = new Set(["presente", "ritardo", "parziale"]);
    const presentCount = recent.filter((e) => presenceStates.has(e.status)).length;
    const attendancePct = recent.length > 0 ? Math.round((presentCount / recent.length) * 100) : null;

    const lastPresence = presenceEvents[0] || null;
    const lastTest = (timeline || []).find((e) => e.type === "test") || null;

    const activeInjury = (injuries || []).find((i) => {
      const st = (i.status || "").toLowerCase();
      return st && !["guarito", "chiuso", "resolved", "closed"].includes(st);
    });

    const today = new Date().toISOString().slice(0, 10);
    const nextComp = (competitions || [])
      .filter((c) => (c.invited_athlete_ids || []).includes(id) && (c.date || "") >= today)
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""))[0] || null;

    return { attendancePct, recentTotal: recent.length, lastPresence, lastTest, activeInjury, nextComp, presenceEvents };
  }, [timeline, injuries, competitions, registers, id]);

  if (!athlete) return <AthleteProfileSkeleton />;

  const dl = (fmt) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/athlete/${id}?fmt=${fmt}`;
    window.open(url, "_blank");
  };

  const groupNames = ((athlete.group_ids?.length ? athlete.group_ids : (athlete.group_id ? [athlete.group_id] : []))
    .map((id) => (groups.find((g) => g.id === id) || {}).name).filter(Boolean));
  const groupName = groupNames.join(" · ") || undefined;
  // v2.1.1 · athlete_weapons attive (primaria per prima) è l'unica fonte. Nessun fallback su athletes.weapon legacy.
  const activeWeapons = Array.from(new Map((weaponRows || []).filter((w) => w.status === "attiva" || w.status === "secondaria").map((w) => [w.weapon_code, w])).values())
    .sort((a, b) => (a.is_primary ? -1 : b.is_primary ? 1 : 0));
  const weaponsList = activeWeapons.length > 0
    ? activeWeapons.map((w) => w.weapon_code).join(" · ")
    : "Arma non impostata";

  const statusTone = athlete.status === "disponibile"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : athlete.status === "infortunato"
      ? "bg-red-50 text-red-700 border-red-200"
      : "bg-zinc-100 text-zinc-700 border-zinc-200";

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/atleti" className="text-sm text-zinc-500 flex items-center gap-1 hover:text-black" data-testid="back-to-athletes">
          <ArrowLeft className="w-4 h-4" />Torna agli atleti
        </Link>
        <div className="flex gap-2">
          <Button data-testid="download-pdf" variant="outline" className="h-10" onClick={() => dl("pdf")}><Download className="w-4 h-4 mr-2" />PDF</Button>
          <Button data-testid="download-excel" variant="outline" className="h-10" onClick={() => dl("excel")}><Download className="w-4 h-4 mr-2" />Excel</Button>
          {canEdit && (
            <Button data-testid="delete-athlete-btn" variant="outline" className="h-10 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="w-4 h-4 mr-2" />Elimina
            </Button>
          )}
        </div>
      </div>

      {/* Compact hero above the fold */}
      <div className="bg-white border border-zinc-200 rounded-md p-5">
        <div className="flex items-start gap-5 flex-wrap">
          {/* Photo */}
          <div className="relative group shrink-0">
            {athlete.photo_url ? (
              <img src={athlete.photo_url} alt="" className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-zinc-100 flex items-center justify-center text-2xl font-heading font-bold" data-testid="athlete-initials">
                {athlete.first_name?.[0]}{athlete.last_name?.[0]}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handlePhoto} data-testid="photo-input" />
            {canEdit && (
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="upload-photo-btn"
                className="absolute inset-0 rounded-full flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 text-white"
                title="Carica foto"
              ><Upload className="w-5 h-5" /></button>
            )}
          </div>

          {/* Identity */}
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="athlete-name">
              {athlete.first_name} {athlete.last_name}
            </h1>
            <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="athlete-chips">
              {athlete.category && (
                <Chip>{athlete.category}</Chip>
              )}
              <Chip>{weaponsList}</Chip>
              {groupName && <Chip>{groupName}</Chip>}
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border rounded ${statusTone}`}>
                {STATUS_LABEL[athlete.status] || athlete.status || "—"}
              </span>
              {athlete.monitoring_level && athlete.monitoring_level !== "standard" && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-black text-white rounded">
                  {athlete.monitoring_level}
                </span>
              )}
              {athlete.photo_url && canEdit && (
                <button onClick={removePhoto} data-testid="remove-photo" className="text-[10px] text-zinc-500 hover:text-red-600 underline ml-1">
                  Rimuovi foto
                </button>
              )}
            </div>
          </div>
        </div>

        {/* KPIs above the fold */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="athlete-kpis">
          {kpis.attendancePct !== null && (
            <Kpi
              icon={CalendarCheck}
              label="Presenze 30gg"
              value={`${kpis.attendancePct}%`}
              hint={`${kpis.recentTotal} sedute rilevate`}
              tone={kpis.attendancePct >= 75 ? "emerald" : kpis.attendancePct >= 50 ? "amber" : "red"}
              testId="kpi-attendance"
            />
          )}
          {kpis.lastPresence && (
            <Kpi
              icon={TrendingUp}
              label="Ultima presenza"
              value={labelDate(kpis.lastPresence.date)}
              hint={kpis.lastPresence.status || "presente"}
              testId="kpi-last-presence"
            />
          )}
          {kpis.lastTest && (
            <Kpi
              icon={ClipboardList}
              label="Ultimo test CSP Lab"
              value={kpis.lastTest.data?.test_name ? `${kpis.lastTest.data.test_name} · ${fmtVal(kpis.lastTest.data.best)} ${kpis.lastTest.data.unit || ""}` : kpis.lastTest.title}
              hint={`${labelDate(kpis.lastTest.date)}${kpis.lastTest.data?.equipment_name ? ` · ${kpis.lastTest.data.equipment_name}` : ""}`}
              testId="kpi-last-test"
            />
          )}
          {kpis.activeInjury && (
            <Kpi
              icon={HeartPulse}
              label="Infortunio attivo"
              value={kpis.activeInjury.body_part || kpis.activeInjury.type || "—"}
              hint={`${kpis.activeInjury.severity || ""} · ${(kpis.activeInjury.status || "").replace("_", " ")}`.trim()}
              tone="red"
              testId="kpi-injury"
            />
          )}
          {kpis.nextComp && (
            <Kpi
              icon={Trophy}
              label="Prossima gara"
              value={labelDate(kpis.nextComp.date)}
              hint={kpis.nextComp.name}
              tone="amber"
              testId="kpi-next-comp"
            />
          )}
        </div>
      </div>

      {/* Tabs — 4 max */}
      <Tabs defaultValue="profile">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="profile" data-testid="tab-profile">Profilo</TabsTrigger>
          <TabsTrigger value="presence" data-testid="tab-presence">Presenze</TabsTrigger>
          <TabsTrigger value="csplab" data-testid="tab-csplab">CSP Lab</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Attività</TabsTrigger>
          {canEdit && <TabsTrigger value="skills" data-testid="tab-skills">Competenze</TabsTrigger>}
        </TabsList>

        {canEdit && <TabsContent value="skills" className="pt-5"><SkillProfile athleteId={id} /></TabsContent>}

        {/* --- PROFILO --- */}
        <TabsContent value="profile" className="pt-5 space-y-6">
          <div className="bg-white border border-zinc-200 rounded-md p-5">
            <SectionTitle>Anagrafica</SectionTitle>
            <fieldset disabled={!canEdit} data-testid="athlete-anagrafica-fields" className="grid lg:grid-cols-2 gap-4 min-w-0">
              <div><Label>Nome</Label><Input value={athlete.first_name || ""} onChange={(e) => setAthlete({...athlete, first_name: e.target.value})} /></div>
              <div><Label>Cognome</Label><Input value={athlete.last_name || ""} onChange={(e) => setAthlete({...athlete, last_name: e.target.value})} /></div>
              <div><Label>Data di nascita</Label><Input type="date" value={athlete.birth_date || ""} onChange={(e) => setAthlete({...athlete, birth_date: e.target.value})} /></div>
              <div>
                <Label>Sesso</Label>
                <Select value={athlete.gender || ""} onValueChange={(v) => setAthlete({...athlete, gender: v})}>
                  <SelectTrigger data-testid="athlete-gender"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschile</SelectItem>
                    <SelectItem value="F">Femminile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Telefono</Label><Input value={athlete.phone || ""} onChange={(e) => setAthlete({...athlete, phone: e.target.value})} /></div>
              <div><Label>Email</Label><Input value={athlete.email || ""} onChange={(e) => setAthlete({...athlete, email: e.target.value})} /></div>
              <div><Label>Contatto emergenza</Label><Input value={athlete.emergency_contact || ""} onChange={(e) => setAthlete({...athlete, emergency_contact: e.target.value})} /></div>
              <div>
                <Label>Mano dominante</Label>
                <Select value={athlete.dominant_hand || "destro"} onValueChange={(v) => setAthlete({...athlete, dominant_hand: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="destro">Destro</SelectItem>
                    <SelectItem value="mancino">Mancino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={athlete.status} onValueChange={(v) => setAthlete({...athlete, status: v})}>
                  <SelectTrigger data-testid="athlete-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disponibile">Disponibile</SelectItem>
                    <SelectItem value="allenamento_ridotto">Allenamento ridotto</SelectItem>
                    <SelectItem value="infortunato">Infortunato</SelectItem>
                    <SelectItem value="return_to_play">Return to play</SelectItem>
                    <SelectItem value="assente">Assente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Livello monitoraggio</Label>
                <Select value={athlete.monitoring_level} onValueChange={(v) => setAthlete({...athlete, monitoring_level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="competitive">Competitive</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Attività</Label>
                <Select
                  value={athlete.activity_type || "__unset"}
                  onValueChange={(v) => setAthlete({...athlete, activity_type: v === "__unset" ? null : v})}
                >
                  <SelectTrigger data-testid="athlete-activity-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agonista" data-testid="activity-agonista">Agonista</SelectItem>
                    <SelectItem value="non_agonista" data-testid="activity-non-agonista">Non agonista</SelectItem>
                    <SelectItem value="__unset" data-testid="activity-unset">Non impostato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gruppi CSP (tecnici)</Label>
                <div className="flex flex-wrap gap-2 mt-1" data-testid="athlete-groups-editor">
                  {groups.map((g) => {
                    const cur = athlete.group_ids?.length ? athlete.group_ids : (athlete.group_id ? [athlete.group_id] : []);
                    const on = cur.includes(g.id);
                    return (
                      <button key={g.id} type="button" data-testid={`athlete-group-toggle-${g.id}`}
                        onClick={() => {
                          const set = new Set(cur);
                          on ? set.delete(g.id) : set.add(g.id);
                          const ids = [...set];
                          setAthlete({ ...athlete, group_ids: ids, group_id: ids[0] || null });
                        }}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}>
                        {g.name}
                      </button>
                    );
                  })}
                  {groups.length === 0 && <span className="text-xs text-zinc-400">Nessun gruppo disponibile</span>}
                </div>
                {athlete.golee_groups?.length > 0 && (
                  <div className="mt-2 text-[11px] text-zinc-500" data-testid="athlete-golee-groups">
                    <span className="uppercase tracking-wider">Gruppi Golee (import):</span>{" "}
                    {athlete.golee_groups.map((g, i) => (
                      <span key={i} className="inline-block bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5 mr-1">{g}</span>
                    ))}
                    <span className="block text-zinc-400 mt-0.5">Sola lettura · gestiti dall'import Golee</span>
                  </div>
                )}
              </div>
              <div><Label>Anni di scherma</Label><Input type="number" value={athlete.years_fencing || 0} onChange={(e) => setAthlete({...athlete, years_fencing: parseInt(e.target.value || 0)})} /></div>
              <div><Label>Livello tecnico</Label><Input value={athlete.technical_level || ""} onChange={(e) => setAthlete({...athlete, technical_level: e.target.value})} /></div>
            </fieldset>
            {canEdit && <Button data-testid="save-athlete" onClick={save} className="mt-5 h-11 bg-black hover:bg-zinc-800">Salva modifiche</Button>}
          </div>

          <div className="bg-white border border-zinc-200 rounded-md p-5">
            <SectionTitle>Categoria</SectionTitle>
            <AthleteCategoryPanel athlete={athlete} onRecomputed={load} />
          </div>

          <div className="bg-white border border-zinc-200 rounded-md p-5">
            <SectionTitle>Armi</SectionTitle>
            <AthleteWeaponsPanel athleteId={id} canEdit={canEdit} onSaved={load} />
          </div>

          <div className="bg-white border border-zinc-200 rounded-md p-5">
            <SectionTitle>Profilo paralimpico</SectionTitle>
            <ParalympicProfileSection athleteId={id} canEdit={canEdit} />
          </div>

          <div className="bg-white border border-zinc-200 rounded-md p-5">
            <SectionTitle>Note mediche</SectionTitle>
            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <Label>Cert. medico scadenza</Label>
                <Input type="date" data-testid="medical-cert-expiry" value={athlete.medical_cert_expiry || ""} onChange={(e) => setAthlete({...athlete, medical_cert_expiry: e.target.value})} />
              </div>
              <div className="lg:col-span-2">
                <Label>Note mediche</Label>
                <Textarea rows={3} value={athlete.medical_notes || ""} onChange={(e) => setAthlete({...athlete, medical_notes: e.target.value})} />
              </div>
            </div>
            <Button variant="outline" onClick={save} className="mt-4 h-10">Salva note</Button>
          </div>
        </TabsContent>

        {/* --- PRESENZE --- */}
        <TabsContent value="presence" className="pt-5">
          <PresenceTab
            presenceEvents={kpis.presenceEvents || []}
            attendancePct={kpis.attendancePct}
            recentTotal={kpis.recentTotal}
          />
        </TabsContent>

        {/* --- CSP LAB (AI + Obiettivi) --- */}
        <TabsContent value="csplab" className="pt-5 space-y-6">
          <AthleteTestSummary athleteId={id} summary={testSummary} />
          <div className="bg-white border border-zinc-200 rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <SectionTitle noMargin><Sparkles className="w-4 h-4 inline text-[#0052FF] -mt-0.5 mr-1" />Suggerimenti AI Coach</SectionTitle>
              {canEdit && (
                <Button data-testid="ai-generate" onClick={generateAI} disabled={aiLoading} className="h-10 bg-black hover:bg-zinc-800">
                  {aiLoading ? "Generazione…" : "Genera analisi"}
                </Button>
              )}
            </div>
            {!aiSuggestion && !aiLoading && <div className="text-sm text-zinc-500">Nessuna analisi disponibile. Genera suggerimenti personalizzati.</div>}
            {aiSuggestion && (
              <div className="space-y-2">
                <div className="text-xs text-zinc-500 font-mono">Generato {aiSuggestion.created_at?.slice(0,16).replace("T"," ")} · {aiSuggestion.model}</div>
                <div className="whitespace-pre-wrap text-sm text-zinc-800 bg-zinc-50 border border-zinc-100 rounded p-4">{aiSuggestion.content}</div>
              </div>
            )}
          </div>

          <div className="bg-white border border-zinc-200 rounded-md p-5">
            <SectionTitle>Obiettivi</SectionTitle>
            {canEdit && (
              <div className="grid grid-cols-3 gap-3 mb-3">
                <Input placeholder="Descrizione" value={newGoal.description} onChange={(e) => setNewGoal({...newGoal, description: e.target.value})} />
                <Input placeholder="Categoria" value={newGoal.category} onChange={(e) => setNewGoal({...newGoal, category: e.target.value})} />
                <Input type="date" value={newGoal.deadline} onChange={(e) => setNewGoal({...newGoal, deadline: e.target.value})} />
              </div>
            )}
            {canEdit && <Button data-testid="add-goal" onClick={addGoal} variant="outline" className="mb-4 h-10">Aggiungi obiettivo</Button>}
            <div className="space-y-2">
              {goals.length === 0 && <div className="text-sm text-zinc-500">Nessun obiettivo impostato.</div>}
              {goals.map((g) => (
                <div key={g.id} className="border border-zinc-100 rounded p-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{g.description}</div>
                    <div className="text-xs text-zinc-500 font-mono">{g.category || "—"} · scadenza {g.deadline || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${g.completed ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                      {g.completed ? "Completato" : "Aperto"}
                    </span>
                    {canEdit && <button onClick={() => deleteGoal(g.id)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* --- ATTIVITÀ (Timeline + Infortuni + Gare) --- */}
        <TabsContent value="activity" className="pt-5 space-y-6">
          <AthleteLessonsCard athleteId={id} />
          <AthleteBoutsCard athleteId={id} />
          {injuries.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-md p-5">
              <SectionTitle>Infortuni · {injuries.length}</SectionTitle>
              <div className="space-y-2">
                {injuries.map((inj) => (
                  <div key={inj.id} className="border border-zinc-100 rounded p-3 flex items-center gap-3" data-testid={`injury-${inj.id}`}>
                    <HeartPulse className="w-4 h-4 text-red-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{inj.body_part || "—"} <span className="text-xs text-zinc-500 font-mono">({inj.type})</span></div>
                      <div className="text-xs text-zinc-500 font-mono">{labelDate(inj.date)} · {inj.severity} · {(inj.status || "").replace("_"," ")}</div>
                    </div>
                    {inj.estimated_return && <div className="text-xs text-zinc-500 font-mono">→ {labelDate(inj.estimated_return)}</div>}
                  </div>
                ))}
              </div>
              <Link to="/infortuni" className="mt-3 inline-block text-xs uppercase tracking-wider text-[#0052FF] hover:underline">Gestisci infortuni →</Link>
            </div>
          )}

          {competitions.filter((c) => (c.invited_athlete_ids || []).includes(id)).length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-md p-5">
              <SectionTitle>Gare</SectionTitle>
              <div className="space-y-2">
                {competitions
                  .filter((c) => (c.invited_athlete_ids || []).includes(id))
                  .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
                  .slice(0, 8)
                  .map((c) => (
                    <div key={c.id} className="border border-zinc-100 rounded p-3 flex items-center gap-3" data-testid={`comp-${c.id}`}>
                      <Trophy className="w-4 h-4 text-amber-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{c.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{labelDate(c.date)}{c.location ? ` · ${c.location}` : ""}{c.weapon ? ` · ${c.weapon}` : ""}</div>
                      </div>
                    </div>
                  ))}
              </div>
              <Link to="/gare" className="mt-3 inline-block text-xs uppercase tracking-wider text-[#0052FF] hover:underline">Tutte le gare →</Link>
            </div>
          )}

          <div className="bg-white border border-zinc-200 rounded-md p-5">
            <SectionTitle>Timeline completa</SectionTitle>
            <div className="space-y-2">
              {timeline.length === 0 && <div className="text-sm text-zinc-500">Nessun evento registrato.</div>}
              {timeline.slice(0, 40).map((e, i) => (
                <div key={i} className="border border-zinc-100 rounded p-3 flex items-center gap-4" data-testid={e.type === "test" ? `timeline-test-${e.data?.id}` : undefined}>
                  <span className="text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-700 px-2 py-1 rounded font-mono shrink-0">{e.type}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{e.title}</div>
                    <div className="text-xs text-zinc-500 font-mono">{e.date?.slice(0, 10)}{e.type === "test" && e.data?.equipment_name ? ` · ${e.data.equipment_name}` : ""}{e.type === "test" && e.data?.side ? ` · ${e.data.side}` : ""}</div>
                  </div>
                  {e.type === "test" && e.data?.test_id && (
                    <Link to={`/test?tab=history&athlete=${id}&test=${e.data.test_id}`} data-testid={`timeline-test-open-${e.data.id}`} className="text-xs uppercase tracking-wider text-[#0052FF] hover:underline shrink-0">Apri analisi →</Link>
                  )}
                </div>
              ))}
              {timeline.length > 40 && (
                <div className="text-xs text-zinc-500 pt-2">Mostrati gli ultimi 40 eventi.</div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <DeleteAthleteDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        athleteId={id}
        label={`${athlete.first_name} ${athlete.last_name}`}
        onConfirm={doDelete}
        onArchived={() => nav("/atleti")}
      />
    </div>
  );
}

function Chip({ children }) {
  return <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-zinc-200 rounded bg-white capitalize">{children}</span>;
}

function SectionTitle({ children, noMargin }) {
  return <h3 className={`font-heading font-semibold tracking-tight text-sm uppercase text-zinc-700 ${noMargin ? "" : "mb-3"}`}>{children}</h3>;
}

function Kpi({ icon: Icon, label, value, hint, tone, testId }) {
  const toneMap = {
    emerald: "border-emerald-200 bg-emerald-50/50",
    amber: "border-amber-200 bg-amber-50/50",
    red: "border-red-200 bg-red-50/50",
  };
  return (
    <div data-testid={testId} className={`border rounded-md p-3 ${toneMap[tone] || "border-zinc-200 bg-white"}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="mt-1 font-heading text-xl font-bold leading-tight break-words" title={typeof value === "string" ? value : undefined}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 truncate mt-0.5">{hint}</div>}
    </div>
  );
}

function PresenceTab({ presenceEvents, attendancePct, recentTotal }) {
  const grouped = (presenceEvents || []).slice(0, 30);
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5 space-y-4">
      <div className="flex items-baseline gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Ultimi 30 giorni</div>
          <div className="font-heading text-3xl font-bold">
            {attendancePct !== null && attendancePct !== undefined ? `${attendancePct}%` : "—"}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">{recentTotal || 0} sedute rilevate</div>
        </div>
      </div>
      <div>
        <SectionTitle>Storico presenze</SectionTitle>
        <div className="space-y-1.5">
          {grouped.length === 0 && <div className="text-sm text-zinc-500">Nessuna presenza registrata.</div>}
          {grouped.map((p, i) => (
            <div key={i} className="flex items-center gap-3 border border-zinc-100 rounded px-3 py-2" data-testid={`presence-row-${i}`}>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                p.status === "presente" ? "bg-emerald-50 text-emerald-700" :
                p.status === "assente" ? "bg-red-50 text-red-700" :
                p.status === "giustificato" ? "bg-zinc-100 text-zinc-700" :
                "bg-amber-50 text-amber-800"
              }`}>{p.status}</span>
              <div className="text-xs text-zinc-500 font-mono">{p.date}</div>
            </div>
          ))}
        </div>
        {(presenceEvents || []).length > 30 && (
          <div className="text-xs text-zinc-500 mt-2">Mostrate le ultime 30 · Vai in <Link className="text-[#0052FF] underline" to="/presenze">Presenze</Link> per lo storico completo.</div>
        )}
      </div>
    </div>
  );
}
